import { useEffect, useState } from "react";

// Pre-game coin-flip ceremony for a fresh LIVE game (shown whether or not
// the announcer is on). The winner (who throws first) is already decided;
// the coin bobs and flips for a beat, lands, reveals the winner, then the
// overlay auto-dismisses. Full-screen; tap to skip.

type Props = {
  winner: 1 | 2;
  winnerLabel: string;
  onDone: () => void;
};

const SPIN_MS = 1900;
const HOLD_MS = 3000;

export function IntroCoinFlip({ winnerLabel, onDone }: Props) {
  const [landed, setLanded] = useState(false);

  useEffect(() => {
    const t1 = setTimeout(() => setLanded(true), SPIN_MS);
    const t2 = setTimeout(onDone, SPIN_MS + HOLD_MS);
    return () => {
      clearTimeout(t1);
      clearTimeout(t2);
    };
  }, [onDone]);

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/85 backdrop-blur-sm cursor-pointer"
      onClick={onDone}
      role="dialog"
      aria-label="Coin toss"
    >
      <style>{`
        @keyframes ch-coin-flip { from { transform: rotateX(0deg); } to { transform: rotateX(1800deg); } }
        @keyframes ch-coin-arc { 0%,100% { transform: translateY(0); } 50% { transform: translateY(-30px); } }
      `}</style>
      <div className="text-center text-white space-y-7 px-6">
        <div className="text-xs font-display uppercase tracking-[0.3em] text-white/60">
          Coin Toss
        </div>

        <div
          className="flex items-center justify-center h-40"
          style={{ perspective: "700px" }}
        >
          <div
            style={{
              animation: landed
                ? undefined
                : "ch-coin-arc 0.7s ease-in-out infinite",
            }}
          >
            <div
              className="relative flex h-28 w-28 items-center justify-center rounded-full border-4 border-yellow-600 bg-gradient-to-br from-yellow-200 to-yellow-500 text-4xl shadow-[0_0_50px_rgba(250,204,21,0.5)]"
              style={{
                transformStyle: "preserve-3d",
                animation: landed
                  ? undefined
                  : "ch-coin-flip 0.55s linear infinite",
              }}
            >
              🌽
            </div>
          </div>
        </div>

        {landed ? (
          <div className="space-y-1">
            <div className="text-3xl font-display uppercase tracking-tight leading-none">
              {winnerLabel}
            </div>
            <div className="text-sm font-display uppercase tracking-widest text-primary pt-1">
              wins the toss · throws first
            </div>
            <div className="text-[10px] text-white/40 pt-3">tap to skip</div>
          </div>
        ) : (
          <div className="text-sm font-display uppercase tracking-widest text-white/70">
            Flipping…
          </div>
        )}
      </div>
    </div>
  );
}
